import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { UserRole } from '../../types/database';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireStaff?: boolean;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({
  children,
  allowedRoles,
  requireStaff,
  requireAdmin,
}: ProtectedRouteProps) {
  const { user, loading, isStaff, isAdmin, roles } = useAuth();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Safety: if auth loading takes more than 12s, stop waiting
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      console.warn('[ProtectedRoute] Auth loading timed out, redirecting to login');
      setTimedOut(true);
    }, 12000);
    return () => clearTimeout(t);
  }, [loading]);

  if (loading && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0b1929]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-clairBlue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Verifying security session...</p>
        </div>
      </div>
    );
  }

  // Not authenticated (or timed out)
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Staff requirement
  if (requireStaff && !isStaff) {
    return <Navigate to="/portal" replace />;
  }

  // Admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/portal" replace />;
  }

  // Specific roles requirement
  if (allowedRoles && allowedRoles.length > 0) {
    const hasAllowed = allowedRoles.some((role) => roles.includes(role));
    if (!hasAllowed) {
      return <Navigate to="/portal" replace />;
    }
  }

  return <>{children}</>;
}
